import { useState } from "react";
import { CommonModelDiffRunExecution, DiffExecutionModelResult } from "../entities";
import CommonModelDiffTypeaheads from "./compare-common-models/CommonModelDiffTypeaheads";
import ComparisonSummary from "./comparison-summary/ComparisonSummary";
import { Text } from "@merge-api/merge-javascript-shared";
import { DiffCategoryTypes } from "../entities";
import { computeCount } from "../helpers";
import ModelDiffView from "./model-diff/ModelDiffView";

interface CommonModelDiffDrawerProps {
  candidateTitle?: string;
  diffRun: CommonModelDiffRunExecution;
  integrationID: string;
}

const CommonModelDiffDrawer = ({
  candidateTitle = "Staged",
  diffRun,
  integrationID,
}: CommonModelDiffDrawerProps) => {
  // state for selected models
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [selectedDiffType, setSelectedDiffType] = useState<DiffCategoryTypes | null>(null);

  // const derived from state
  const modelNames = diffRun.results
    ? diffRun.results.models?.map((result) => result.model_name)
    : [];
  const displayableModels = selectedModels.length > 0 ? selectedModels : modelNames || [];
  const modelsArray: DiffExecutionModelResult[] = diffRun.results.models ?? [];

  // once a specific difference type is selected, drop models with none of that type so
  // large runs don't force scrolling past hundreds of models with nothing matching
  const visibleModels = displayableModels.filter((modelName) => {
    if (!selectedDiffType) return true;
    const modelResult = modelsArray.find((m) => m.model_name === modelName);
    return modelResult ? computeCount(modelResult, selectedDiffType) > 0 : true;
  });

  // Handlers for selections
  const handleModelSelectChange = (models: string[]) => {
    setSelectedModels(models);
  };

  const handleDiffTypeChange = (diffType: DiffCategoryTypes | null) => {
    setSelectedDiffType(diffType);
  };

  return (
    <div className="p-5">
      <Text as="div" variant="h4" className="mb-4">
        Comparison summary
      </Text>
      <ComparisonSummary
        candidateTitle={candidateTitle}
        diffRun={diffRun}
        integrationID={integrationID}
      />
      <Text as="div" variant="h4" className="mt-6 mb-4">
        Compare Common Models
      </Text>
      <CommonModelDiffTypeaheads
        modelNames={modelNames || []}
        selectedModels={selectedModels}
        selectedDiffType={selectedDiffType}
        onModelSelectChange={handleModelSelectChange}
        onDiffTypeChange={handleDiffTypeChange}
      />
      {selectedDiffType && visibleModels.length === 0 && (
        <Text as="div" className="mt-4 text-gray-40">
          No common models found for the selected difference type.
        </Text>
      )}
      {visibleModels.length > 0 && (
        <div className="space-y-4 mt-4">
          {visibleModels.map((modelName) => {
            const modelResult = modelsArray.find((m) => m.model_name === modelName);
            if (!modelResult) {
              return null;
            }
            return (
              <ModelDiffView
                key={modelName}
                diffRunID={diffRun.id}
                modelName={modelName}
                modelResult={modelResult}
                diffType={selectedDiffType}
                collapseUnchangedLines={selectedDiffType !== null}
                candidateTitle={candidateTitle}
                stagedBlueprintVersionID={diffRun.staged_blueprint_version_id}
                publishedBlueprintVersionID={diffRun.published_blueprint_version_id}
                integrationID={integrationID}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CommonModelDiffDrawer;
